/**
 * Step 8 — Integrations [TYPE B — Optional]
 *
 * Connect finance, time, expense and purchasing systems.
 * Shows empty state by default — clients may not have credentials on day one.
 * Has inline editor (company sheet opens inside the overlay).
 */

import { useState } from 'react';
import { Plug, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StepWrapper } from '../StepWrapper';
import { SettingsIntegrations } from '@/pages/settings/settings-integrations';

interface StepProps {
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  isLastStep: boolean;
}

export function Step8Integrations({ onNext, onBack, onSkip, isLastStep }: StepProps) {
  const [showSettings, setShowSettings] = useState(false);

  return (
    <StepWrapper
      barState="default"
      barMessage="Connect your finance and time systems first — downstream settings like GP method and budget mode depend on what's connected."
      showBack={false}
      showSkip={true}
      nextLabel={isLastStep ? 'Finish Setup →' : 'Next Step →'}
      onNext={onNext}
      onBack={onBack}
      onSkip={onSkip}
    >
      {!showSettings ? (
        /* Empty state */
        <div className="flex flex-col items-center justify-center py-20 space-y-5 text-center px-8">
          <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Plug className="h-8 w-8 text-primary" />
          </div>
          <div className="space-y-2 max-w-md">
            <h3 className="text-lg font-semibold text-foreground">Connect Your Systems First</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              We recommend connecting Access Projects to your finance system,
              timesheet tool, expense platform and purchasing software before
              configuring anything else. Settings like GP calculation method
              and budget mode depend on what's connected. You'll need your
              integration credentials to hand. You can also skip this and
              return later from{' '}
              <span className="font-medium">Settings → Integrations</span>.
            </p>
          </div>
          <Button onClick={() => setShowSettings(true)} className="gap-2">
            Set Up Integrations Now
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        /* Embedded settings page */
        <div className="rounded-lg border border-border overflow-hidden bg-background shadow-sm">
          <SettingsIntegrations />
        </div>
      )}
    </StepWrapper>
  );
}
